'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Play, Volume2, VolumeX, Maximize2, Share2, Heart, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { streamService, Stream } from '@/lib/services/streamService';
import StreamPlayerModal from './StreamPlayerModal';

export default function HeroFeaturedStream() {
  const [muted, setMuted] = useState(true);
  const [liked, setLiked] = useState(false);
  const [featuredStreams, setFeaturedStreams] = useState<Stream[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [autoAdvancePaused, setAutoAdvancePaused] = useState(false);

  useEffect(() => {
    // Load multiple featured streams for carousel
    streamService.getTVChannels().then((data) => {
      // Use first 5 live streams as featured carousel items
      const live = data.filter((s) => s.streamStatus === 'live').slice(0, 5);
      if (live.length === 0) {
        // Fallback: use first 5 streams
        setFeaturedStreams(data.slice(0, 5));
      } else {
        setFeaturedStreams(live);
      }
      setLoading(false);
    });
  }, []);

  const featuredStream = featuredStreams[currentIndex] ?? null;

  const prev = useCallback(() => {
    setCurrentIndex((i) => (i === 0 ? featuredStreams.length - 1 : i - 1));
  }, [featuredStreams.length]);

  const next = useCallback(() => {
    setCurrentIndex((i) => (i === featuredStreams.length - 1 ? 0 : i + 1));
  }, [featuredStreams.length]);

  // Auto-advance carousel every 8 seconds
  useEffect(() => {
    if (featuredStreams.length <= 1 || autoAdvancePaused) return;
    const timer = setInterval(next, 8000);
    return () => clearInterval(timer);
  }, [next, featuredStreams.length, autoAdvancePaused]);

  if (loading) {
    return (
      <div className="relative rounded-2xl overflow-hidden bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] animate-pulse">
        <div className="aspect-video w-full max-h-[420px] bg-[rgb(24,24,24)]" />
        <div className="flex items-center gap-3 px-4 py-3 bg-[rgb(15,15,15)]">
          <div className="h-9 w-28 bg-[rgb(24,24,24)] rounded-lg" />
        </div>
      </div>
    );
  }

  if (!featuredStream) return null;

  function formatViewers(n: number): string {
    return n.toLocaleString();
  }

  const openPlayer = () => {
    setPlayerOpen(true);
    setAutoAdvancePaused(true);
  };
  const closePlayer = () => setPlayerOpen(false);

  return (
    <>
      <div className="relative rounded-2xl overflow-hidden bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] group">
        {/* Thumbnail / Player Area — clickable */}
        <div
          className="relative aspect-video w-full max-h-[420px] overflow-hidden cursor-pointer"
          onClick={openPlayer}
        >
          {featuredStream?.thumbnail ? (
            <img
              src={featuredStream.thumbnail}
              alt={featuredStream.thumbnailAlt ?? featuredStream.name}
              className="w-full h-full object-cover transition-opacity duration-500"
            />
          ) : (
            <div className="w-full h-full bg-[rgb(24,24,24)] flex items-center justify-center">
              <span className="text-6xl">{featuredStream?.flag}</span>
            </div>
          )}

          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[rgba(0,0,0,0.6)] via-transparent to-transparent" />

          {/* LIVE badge */}
          <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-red-600 text-white text-xs font-700 px-2.5 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-white live-pulse" />
            LIVE
          </div>

          {/* Featured label */}
          <div className="absolute top-4 right-4 bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] text-white text-xs font-600 px-2.5 py-1 rounded-full">
            ⭐ Featured
          </div>

          {/* Carousel Navigation Arrows */}
          {featuredStreams.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100 z-10"
                aria-label="Previous featured channel"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100 z-10"
                aria-label="Next featured channel"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}

          {/* Center Play Button */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={(e) => { e.stopPropagation(); openPlayer(); }}
              className="w-16 h-16 rounded-full bg-[rgba(220,38,38,0.9)] hover:bg-red-600 flex items-center justify-center transition-all duration-150 active:scale-95 shadow-2xl"
            >
              <Play size={28} fill="white" className="text-white ml-1" />
            </button>
          </div>

          {/* Bottom info overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <div className="flex items-end justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-500 text-red-400 bg-[rgba(220,38,38,0.15)] px-2 py-0.5 rounded-full">{featuredStream?.category}</span>
                  <span className="text-xs text-[rgb(163,163,163)]">{featuredStream?.country} {featuredStream?.flag}</span>
                  <span className="text-xs text-[rgb(115,115,115)]">• {featuredStream?.language}</span>
                </div>
                <div className="flex items-center gap-2">
                  {featuredStream?.logoUrl && (
                    <img
                      src={featuredStream.logoUrl}
                      alt={`${featuredStream.name} logo`}
                      className="w-8 h-8 rounded-full object-cover border border-[rgba(255,255,255,0.2)] bg-[rgb(24,24,24)] flex-shrink-0"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <h2 className="text-xl font-700 text-white leading-tight">{featuredStream?.name}</h2>
                </div>
                <p className="text-sm text-[rgb(163,163,163)] mt-1 max-w-lg hidden sm:block">{featuredStream?.description}</p>
              </div>
              <div className="flex items-center gap-1.5 text-white text-sm font-500 flex-shrink-0">
                <Eye size={14} className="text-[rgb(163,163,163)]" />
                <span className="tabular-nums">{formatViewers(featuredStream?.viewers ?? 0)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Controls bar — no uptime */}
        <div className="flex items-center gap-3 px-4 py-3 bg-[rgb(15,15,15)]">
          <button
            onClick={openPlayer}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-600 rounded-lg transition-all duration-150 active:scale-95"
          >
            <Play size={14} fill="white" />
            Watch Live
          </button>
          <button
            onClick={() => setMuted(!muted)}
            className="p-2 text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] rounded-lg transition-all">
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <button
            onClick={openPlayer}
            className="p-2 text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] rounded-lg transition-all"
          >
            <Maximize2 size={16} />
          </button>
          <div className="flex-1" />

          {/* Carousel dots */}
          {featuredStreams.length > 1 && (
            <div className="flex items-center gap-1.5">
              {featuredStreams.map((_, i) => (
                <button
                  key={`dot-${i}`}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentIndex ? 'bg-red-600 w-4' : 'bg-[rgb(64,64,64)] hover:bg-[rgb(115,115,115)]'}`}
                  aria-label={`Go to featured channel ${i + 1}`}
                />
              ))}
            </div>
          )}

          <button
            onClick={() => setLiked(!liked)}
            className={`p-2 rounded-lg transition-all ${liked ? 'text-red-400 bg-[rgba(220,38,38,0.1)]' : 'text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)]'}`}>
            <Heart size={16} fill={liked ? 'currentColor' : 'none'} />
          </button>
          <button className="p-2 text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] rounded-lg transition-all">
            <Share2 size={16} />
          </button>
        </div>
      </div>

      {/* Stream Player Modal */}
      {playerOpen && (
        <StreamPlayerModal
          stream={featuredStream}
          onClose={closePlayer}
        />
      )}
    </>
  );
}